import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, Navigate, useSearchParams } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { DeletedInSalesforceBadge } from "../components/crm/DeletedInSalesforceBadge";
import { InventoryTypeIcon } from "../components/logistics/InventoryTypeIcon";
import { WorkspaceOneDeviceFacts } from "../components/logistics/InventoryWorkspaceOne";
import { RequirePermission } from "../components/RequirePermission";
import { LOGISTICS_INVENTORY_ROUTE, ResourceName } from "../constants/resources";
import {
  CrmObject,
  LogisticsInventoryDeviceFilter,
  type LogisticsWorkspaceOneDevicesQuery,
  LogisticsWorkspaceOneFleetState,
  LogisticsWorkspaceOneMatchState,
} from "../generated/graphql";
import { CRM_COLORS, CRM_LINK_SX, crmRecordPath, formatCrmCount, formatCrmDateTime } from "../lib/crm";
import { logisticsInventoryItemPath } from "../lib/logistics";

type Overview = LogisticsWorkspaceOneDevicesQuery["logisticsWorkspaceOneDevices"];
type ProviderRow = Overview["devices"][number];
const cellSx = {
  verticalAlign: "top",
  py: "8px",
  px: "12px",
  fontSize: "13px",
  color: CRM_COLORS.text,
  whiteSpace: "nowrap",
};
const APP_BUNDLES = [
  ["com.media4care.b2clic", "senior"],
  ["com.media4care.b2blic", "caregiver"],
] as const;

const DeviceIdentity = ({ row }: { row: ProviderRow }) => {
  const translate = useTranslate();
  const unknown = translate("logistics.workspaceOne.unknown");
  return (
    <>
      <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>{row.device.name || unknown}</Typography>
      <Box
        component="dl"
        sx={{ display: "grid", gap: "2px 8px", gridTemplateColumns: "max-content 1fr", m: "8px 0 0" }}
      >
        {[
          ["imei", row.device.imei],
          ["serialNumber", row.device.serialNumber],
          ["organizationGroupName", row.device.organizationGroupName],
        ].map(([field, value]) => (
          <Box key={field} sx={{ display: "contents" }}>
            <Typography component="dt" sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
              {translate(`logistics.workspaceOne.overview.${field}`)}
            </Typography>
            <Box
              component="dd"
              aria-label={translate(`logistics.workspaceOne.overview.${field}`)}
              sx={{ m: 0, overflowWrap: "anywhere" }}
            >
              {value || unknown}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

const DeviceCustomer = ({ row }: { row: ProviderRow }) => {
  const translate = useTranslate();
  const { canAccess: canOpenCrm } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const customer = row.matchState === LogisticsWorkspaceOneMatchState.Linked ? row.customer : null;
  return (
    <>
      {customer ? (
        canOpenCrm ? (
          <Link
            component={RouterLink}
            to={crmRecordPath(CrmObject.Account, customer.accountSalesforceId)}
            sx={CRM_LINK_SX}
          >
            {customer.accountName || customer.accountSalesforceId}
          </Link>
        ) : (
          customer.accountName || customer.accountSalesforceId
        )
      ) : (
        <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
          {translate("logistics.workspaceOne.overview.noCustomer")}
        </Typography>
      )}
    </>
  );
};

const DeviceContradictions = ({ row }: { row: ProviderRow }) => {
  const translate = useTranslate();
  return (
    <>
      <Box sx={{ display: "grid", gap: "6px" }}>
        {row.contradictions.length
          ? row.contradictions.map(flag => (
              <Typography key={flag} sx={{ fontSize: "13px", color: CRM_COLORS.text }}>
                {translate(`logistics.workspaceOne.overview.contradictions.${flag}`)}
              </Typography>
            ))
          : translate("logistics.workspaceOne.overview.noContradictions")}
      </Box>
    </>
  );
};

export const WorkspaceOneInventoryContext = ({
  inventoryId,
  overview,
}: {
  inventoryId: string;
  overview?: Overview;
}) => {
  const translate = useTranslate();
  const row =
    overview?.state === LogisticsWorkspaceOneFleetState.Available
      ? overview.devices.find(
          item => item.matchState === LogisticsWorkspaceOneMatchState.Linked && item.inventory?.id === inventoryId
        )
      : undefined;
  if (!row) return null;
  return (
    <>
      <DeviceIdentity row={row} />
      <Typography sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
        {translate("logistics.workspaceOne.overview.columns.customer")}
      </Typography>
      <DeviceCustomer row={row} />
      <DeviceContradictions row={row} />
    </>
  );
};

const DeviceTable = ({ overview }: { overview: Overview }) => {
  const translate = useTranslate();
  const unknown = translate("logistics.workspaceOne.unknown");
  const columns = ["device", "facts", "inventory", "customer", "contradictions", "console"] as const;
  return (
    <TableContainer
      role="region"
      aria-label={translate("logistics.workspaceOne.overview.table")}
      tabIndex={0}
      sx={{ maxHeight: "calc(100vh - 360px)", backgroundColor: CRM_COLORS.cardBackground }}
    >
      <Table size="small" stickyHeader aria-label={translate("logistics.workspaceOne.overview.table")}>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column}
                sx={{ ...cellSx, backgroundColor: CRM_COLORS.tableHeader, color: CRM_COLORS.textWeak, fontWeight: 700 }}
              >
                {translate(`logistics.workspaceOne.overview.columns.${column}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {overview.devices.map(row => {
            const linked = row.matchState === LogisticsWorkspaceOneMatchState.Linked;
            const inventory = linked ? row.inventory : null;
            return (
              <TableRow
                key={row.device.deviceId}
                data-testid="workspace-one-device-row"
                hover
                sx={{ "&:hover": { backgroundColor: CRM_COLORS.rowHover } }}
              >
                <TableCell sx={{ ...cellSx, minWidth: 220 }}>
                  <DeviceIdentity row={row} />
                </TableCell>
                <TableCell sx={{ ...cellSx, minWidth: 340 }}>
                  <Box sx={{ display: "grid", gap: "6px" }}>
                    <WorkspaceOneDeviceFacts device={row.device} syncedAt={overview.syncedAt ?? null} compact />
                  </Box>
                </TableCell>
                <TableCell sx={{ ...cellSx, minWidth: 220 }}>
                  {inventory ? (
                    <Box sx={{ display: "grid", gap: "6px" }}>
                      <Link component={RouterLink} to={logisticsInventoryItemPath(inventory.id)} sx={CRM_LINK_SX}>
                        {inventory.name || translate("logistics.inventory.unnamed")}
                      </Link>
                      <Typography sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
                        {translate("logistics.workspaceOne.overview.hardwareSerialNumber")}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", overflowWrap: "anywhere" }}>
                        {inventory.hardwareSerialNumber || unknown}
                      </Typography>
                      {inventory.deletedInSalesforceAt && (
                        <DeletedInSalesforceBadge deletedAt={inventory.deletedInSalesforceAt} />
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "13px", color: CRM_COLORS.textWeak }}>
                      {translate(`logistics.workspaceOne.overview.matchStates.${row.matchState}`)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ ...cellSx, minWidth: 200 }}>
                  <DeviceCustomer row={row} />
                </TableCell>
                <TableCell sx={{ ...cellSx, minWidth: 220 }}>
                  <DeviceContradictions row={row} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Link
                    href={row.device.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ ...CRM_LINK_SX, display: "inline-flex", alignItems: "center", gap: "4px" }}
                  >
                    {translate("logistics.workspaceOne.open")}
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const WorkspaceOneInventoryOverview = ({
  overview,
  loading,
  error,
}: {
  overview?: Overview;
  loading: boolean;
  error: boolean;
}) => {
  const translate = useTranslate();
  const available = overview?.state === LogisticsWorkspaceOneFleetState.Available;
  const unresolved = overview?.devices.filter(row => row.matchState !== LogisticsWorkspaceOneMatchState.Linked) ?? [];
  return (
    <Box data-testid="workspace-one-summary" sx={{ mt: "12px" }}>
      <CrmCard title={translate("logistics.workspaceOne.title")} icon={<InventoryTypeIcon type="Tablet" size={24} />}>
        <Typography sx={{ mb: "12px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
          {translate("logistics.workspaceOne.overview.syncedAt", {
            time: overview?.syncedAt
              ? formatCrmDateTime(overview.syncedAt)
              : translate("logistics.workspaceOne.unknown"),
          })}
        </Typography>
        {available && overview && (
          <>
            <Box component="dl" sx={{ display: "flex", flexWrap: "wrap", gap: "12px 24px", m: 0 }}>
              {[
                [translate("logistics.workspaceOne.overview.total"), overview.devices.length],
                ...APP_BUNDLES.map(([bundleId, app]) => [
                  translate(`logistics.workspaceOne.apps.${app}`),
                  overview.devices.filter(row => row.device.apps.some(item => item.bundleId === bundleId)).length,
                ]),
              ].map(([label, count]) => (
                <Box key={String(label)}>
                  <Typography component="dt" sx={{ fontSize: "12px", color: CRM_COLORS.label }}>
                    {label}
                  </Typography>
                  <Box component="dd" aria-label={String(label)} sx={{ m: 0, fontSize: "20px", fontWeight: 700 }}>
                    {formatCrmCount(Number(count))}
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography sx={{ mt: "8px", fontSize: "12px", color: CRM_COLORS.textWeak }}>
              {translate("logistics.workspaceOne.overview.appPopulation")}
            </Typography>
          </>
        )}
        {error && <Alert severity="error">{translate("logistics.workspaceOne.overview.loadError")}</Alert>}
        {loading && !overview && <CircularProgress aria-label={translate("logistics.workspaceOne.overview.loading")} />}
        {overview?.stale && (
          <Alert severity="warning" sx={{ mb: "12px" }}>
            {translate("logistics.workspaceOne.stale")}
          </Alert>
        )}
        {overview && !available && (
          <Alert severity="info" data-testid="workspace-one-state">
            {translate(`logistics.workspaceOne.overview.states.${overview.state}`)}
          </Alert>
        )}
        {available && overview?.devices.length === 0 && (
          <Typography sx={{ p: "16px", color: CRM_COLORS.textWeak }}>
            {translate("logistics.workspaceOne.overview.empty")}
          </Typography>
        )}
      </CrmCard>
      {available && overview && unresolved.length > 0 && (
        <Box sx={{ mt: "12px" }}>
          <CrmCard
            flush
            title={translate("logistics.workspaceOne.overview.unresolvedTitle")}
            icon={<InventoryTypeIcon type="Tablet" size={24} />}
          >
            <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("logistics.workspaceOne.overview.unresolvedCount", {
                count: formatCrmCount(unresolved.length),
              })}
            </Typography>
            <DeviceTable overview={{ ...overview, devices: unresolved }} />
          </CrmCard>
        </Box>
      )}
    </Box>
  );
};

const LegacyWorkspaceOneRedirect = () => {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.delete("tab");
  if (next.get("type") !== "Tablet" || next.get("deviceFilter") !== LogisticsInventoryDeviceFilter.WorkspaceOne)
    next.delete("page");
  next.set("type", "Tablet");
  next.set("deviceFilter", LogisticsInventoryDeviceFilter.WorkspaceOne);
  next.delete("inactiveDays");
  return <Navigate to={`${LOGISTICS_INVENTORY_ROUTE}?${next}`} replace />;
};

export const LogisticsWorkspaceOnePage = () => (
  <RequirePermission permission={ResourceName.LogisticsInventory}>
    <LegacyWorkspaceOneRedirect />
  </RequirePermission>
);
